import * as React from 'react';
import axios, { AxiosError } from 'axios';
import { useAuthContext } from 'contexts/AuthContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { TenantData, TenantSearchableResponse } from 'App.types';

interface UseTenantListApiResult<TResponse = any> {
  data: TResponse | undefined;
  error: AxiosError | undefined;
  loading: boolean;
  fetch: (forceRefresh?: boolean) => Promise<void>;
}

// Module-level caches shared across all hook instances.
// resultCache  – stores resolved, region-filtered response data keyed by URL.
// inflightCache – deduplicates concurrent requests for the same URL.
const resultCache = new Map<string, any>();
const inflightCache = new Map<string, Promise<any>>();

/** Clears cached responses. Pass a URL to clear one entry, or omit to clear all. */
export function clearTenantListCache(url?: string) {
  if (url) {
    resultCache.delete(url);
    inflightCache.delete(url);
  } else {
    resultCache.clear();
    inflightCache.clear();
  }
}

/** Returns true for tenants that belong to a supported region (or have no region set). */
function isInSupportedRegion(tenant: TenantData, supportedRegions: string[]): boolean {
  return !tenant.region || supportedRegions.includes(tenant.region);
}

/** Applies region filtering to the raw API response before it is cached. */
function filterByRegion(
  response: TenantSearchableResponse,
  supportedRegions: string[],
): TenantSearchableResponse {
  if (!supportedRegions.length) return response;
  return {
    ...response,
    results: response.results.filter((t) => isInSupportedRegion(t, supportedRegions)),
  };
}

/**
 * Hook for fetching the tenant list.
 * A single axios request is made; the browser follows any 302 redirect to S3
 * transparently, stripping the Authorization header on the cross-origin hop.
 * Results are filtered by tenantSupportedRegions and cached at the module
 * level so subsequent mounts return immediately without a network round-trip.
 */
export function useTenantListApi<TResponse = any>(url: string): UseTenantListApiResult<TResponse> {
  const { idToken } = useAuthContext();
  const { tenantSupportedRegions = [] } = useAppConfigContext();

  // Keep a ref so the callback always sees the latest regions without
  // needing to be recreated every time the config re-renders.
  const supportedRegionsRef = React.useRef(tenantSupportedRegions);
  supportedRegionsRef.current = tenantSupportedRegions;

  const [data, setData] = React.useState<TResponse | undefined>(() => resultCache.get(url));
  const [error, setError] = React.useState<AxiosError | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);

  const fetch = React.useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh && resultCache.has(url)) {
        setData(resultCache.get(url));
        return;
      }

      setLoading(true);
      setError(undefined);

      // forceRefresh must bypass any in-flight request too.
      if (forceRefresh) {
        inflightCache.delete(url);
      }

      try {
        if (!inflightCache.has(url)) {
          inflightCache.set(
            url,
            axios
              .get<TenantSearchableResponse>(url, {
                headers: { Authorization: `Bearer ${idToken}` },
              })
              .then((res) => {
                const filtered = filterByRegion(res.data, supportedRegionsRef.current);
                resultCache.set(url, filtered);
                inflightCache.delete(url);
                return filtered;
              })
              .catch((err) => {
                inflightCache.delete(url);
                throw err;
              }),
          );
        }

        const result = await inflightCache.get(url)!;
        setData(result);
      } catch (err) {
        setError(err as AxiosError);
      } finally {
        setLoading(false);
      }
    },
    [url, idToken],
  );

  return { data, error, loading, fetch };
}

export default useTenantListApi;
